import React, { useEffect, useRef, useState } from "react";
import axios from "../../utils/axiosInstance";
import { PaperPlaneRight, Trash, Warning, Circle, Smiley, SpeakerHigh, SpeakerSlash, Paperclip, X, File as FileIcon, Download } from "@phosphor-icons/react";
import { useChatBadge } from "../../contexts/ChatBadgeContext";
import { ChartBar } from "@phosphor-icons/react";
import { ChannelBar } from "./chat/ChannelBar";
import { PollCard } from "./chat/PollCard";
import { CreatePollModal } from "./chat/CreatePollModal";

const initialChannel = () => {
  try { return new URLSearchParams(window.location.search).get("channel") || "ogolny"; } catch { return "ogolny"; }
};

const REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// Play a soft "ding" using Web Audio API — no external asset needed.
function playDing() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
    setTimeout(() => ctx.close(), 500);
  } catch { /* audio unavailable */ }
}

const fmtBytes = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const isImg = (ct) => (ct || "").startsWith("image/");

// Cache of blob URLs keyed by remote URL so we don't re-download the same file.
const blobUrlCache = new Map();

async function fetchBlobUrl(remoteUrl) {
  if (blobUrlCache.has(remoteUrl)) return blobUrlCache.get(remoteUrl);
  const res = await axios.get(remoteUrl, { responseType: "blob" });
  const objUrl = URL.createObjectURL(res.data);
  blobUrlCache.set(remoteUrl, objUrl);
  return objUrl;
}

/** Image tag that fetches via authorized axios (bearer token) and shows as blob URL. */
function AuthImage({ url, alt, className, onClickPreview }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setErr(false);
    fetchBlobUrl(url)
      .then((u) => { if (!cancelled) setSrc(u); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [url]);
  if (err) {
    return <div className={`${className} bg-red-50 text-red-600 text-[10px] flex items-center justify-center p-2`}>Nie udało się pobrać obrazu</div>;
  }
  if (!src) {
    return <div className={`${className} bg-slate-100 animate-pulse flex items-center justify-center`}><span className="text-[10px] text-slate-400">Ładowanie...</span></div>;
  }
  return <img src={src} alt={alt} className={className} onClick={onClickPreview} />;
}

/** Authorized download via blob so browser sends Bearer token via axios. */
async function downloadAuth(remoteUrl, filename) {
  try {
    const objUrl = await fetchBlobUrl(remoteUrl);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename || "plik";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    alert("Nie udało się pobrać pliku: " + (e.response?.data?.detail || e.message));
  }
}

function MessageAttachment({ att, mine, onOpenImage }) {
  const url = `${window.location.origin}${att.url}`;
  if (isImg(att.content_type)) {
    return (
      <div className="block mt-1.5" data-testid="att-img">
        <AuthImage
          url={url}
          alt={att.name}
          className="max-w-full w-auto h-auto max-h-56 sm:max-h-64 rounded-md border border-black/10 cursor-zoom-in object-contain"
          onClickPreview={() => onOpenImage && onOpenImage({ url, name: att.name, size: att.size })}
        />
        <div className={`text-[10px] mt-0.5 ${mine ? "text-blue-100" : "text-slate-500"}`}>{att.name} · {fmtBytes(att.size)}</div>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => downloadAuth(url, att.name)}
      className={`mt-1.5 inline-flex items-center gap-2 px-2 py-1.5 rounded border ${mine ? "bg-white/10 border-white/20 text-white hover:bg-white/20" : "bg-white border-border text-foreground hover:bg-slate-50"}`}
      data-testid="att-file">
      <FileIcon className="w-4 h-4" />
      <div className="min-w-0 text-left">
        <div className="text-xs font-semibold truncate max-w-[200px]">{att.name}</div>
        <div className={`text-[10px] ${mine ? "text-blue-100" : "text-muted-foreground"}`}>{fmtBytes(att.size)}</div>
      </div>
      <Download className="w-3 h-3 ml-1" />
    </button>
  );
}

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function ChatTab({ user }) {
  const isAdmin = user?.role === "admin";
  const { refresh: refreshBadge } = useChatBadge();
  const [messages, setMessages] = useState([]);
  const [online, setOnline] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [text, setText] = useState("");
  const [isPilne, setIsPilne] = useState(false);
  const [sending, setSending] = useState(false);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem("chat_sound") !== "off"; } catch { return true; }
  });
  const [pickerFor, setPickerFor] = useState(null); // message id
  const [attachment, setAttachment] = useState(null); // {id, name, size, content_type, url}
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {url, name, size} or null
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState(initialChannel);
  const [showPoll, setShowPoll] = useState(false);
  const channelRef = useRef(channelId);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastIdRef = useRef(null);
  const initialLoadRef = useRef(true);
  const forceScrollRef = useRef(true);
  const soundOnRef = useRef(soundOn);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  useEffect(() => {
    try { localStorage.setItem("chat_sound", soundOn ? "on" : "off"); } catch { /* ignore */ }
  }, [soundOn]);

  const loadChannels = async () => {
    try {
      const r = await axios.get("/api/chat/channels");
      const list = r.data || [];
      setChannels(list);
      if (!list.some((c) => c.id === channelRef.current)) selectChannel("ogolny");
    } catch { /* ignore */ }
  };

  const selectChannel = (id) => {
    if (id === channelRef.current) return;
    channelRef.current = id;
    initialLoadRef.current = true;
    forceScrollRef.current = true;
    lastIdRef.current = null;
    setMessages([]);
    setChannelId(id);
  };

  const loadAll = async () => {
    const cid = channelRef.current;
    try {
      const [m, o, t] = await Promise.all([
        axios.get("/api/chat/messages", { params: { channel_id: cid } }),
        axios.get("/api/chat/online"),
        axios.get("/api/chat/typing"),
      ]);
      if (cid !== channelRef.current) return;
      const newMessages = m.data || [];
      // Detect new incoming message (from someone else) to play ding
      if (!initialLoadRef.current && soundOnRef.current && newMessages.length > 0) {
        const last = newMessages[newMessages.length - 1];
        if (last.id !== lastIdRef.current && String(last.author_id) !== String(user?._id || user?.id)) {
          playDing();
        }
      }
      if (newMessages.length > 0) lastIdRef.current = newMessages[newMessages.length - 1].id;
      initialLoadRef.current = false;
      setMessages(newMessages);
      setOnline(o.data || []);
      setTypingUsers(t.data || []);
    } catch { /* ignore */ }
  };

  const markRead = () => axios.post("/api/chat/mark-read", null, { params: { channel_id: channelRef.current } }).catch(() => {});

  // Mark the active channel as read on open / switch, then poll
  useEffect(() => {
    (async () => { await markRead(); refreshBadge(); loadChannels(); })();
    loadAll();
    const iv = setInterval(async () => {
      await loadAll();
      await markRead();
      loadChannels();
    }, 3000);
    return () => { clearInterval(iv); refreshBadge(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  useEffect(() => {
    // Only auto-scroll to bottom when user is ALREADY near the bottom.
    // If user is scrolled up reading history, keep their position.
    const el = scrollRef.current;
    if (!el) return;
    if (forceScrollRef.current && messages.length > 0) {
      // First load / channel switch / own message → jump to the newest message
      forceScrollRef.current = false;
      const toBottom = () => { el.scrollTop = el.scrollHeight; };
      toBottom();
      requestAnimationFrame(toBottom);
      setTimeout(toBottom, 400); // images/attachments may change height
      return;
    }
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distanceFromBottom < 150; // px threshold
    if (nearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Debounced typing ping
  const handleInputChange = (e) => {
    setText(e.target.value);
    if (e.target.value && !typingTimerRef.current) {
      axios.post("/api/chat/typing").catch(() => {});
      typingTimerRef.current = setTimeout(() => { typingTimerRef.current = null; }, 3000);
    }
  };

  const send = async () => {
    if ((!text.trim() && !attachment) || sending) return;
    setSending(true);
    try {
      forceScrollRef.current = true;
      await axios.post("/api/chat/messages", {
        content: text.trim(),
        is_pilne: isPilne,
        attachment_id: attachment?.id || null,
        channel_id: channelRef.current,
      });
      setText("");
      setIsPilne(false);
      setAttachment(null);
      await loadAll();
    } catch (e) { alert("Błąd: " + (e.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      alert("Plik przekracza limit 10 MB");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post("/api/chat/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment(res.data);
    } catch (err) {
      alert("Błąd uploadu: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const delMsg = async (id) => {
    if (!window.confirm("Usunąć?")) return;
    try { await axios.delete(`/api/chat/messages/${id}`); await loadAll(); } catch { /* ignore */ }
  };

  const react = async (id, emoji) => {
    setPickerFor(null);
    try {
      const res = await axios.post(`/api/chat/messages/${id}/react`, { emoji });
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, reactions: res.data.reactions } : m)));
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4" data-testid="chat-tab">
      {/* Chat */}
      <div className="bg-white border border-border rounded-md shadow-sm flex flex-col" style={{ minHeight: 500 }}>
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold">Czat zespołu</h3>
            <p className="text-xs text-muted-foreground" data-testid="chat-active-channel">#{channels.find((c) => c.id === channelId)?.name || "Ogólny"}</p>
          </div>
          <button
            onClick={() => setSoundOn((v) => !v)}
            title={soundOn ? "Wyłącz dźwięk" : "Włącz dźwięk"}
            aria-label={soundOn ? "Wyłącz dźwięk" : "Włącz dźwięk"}
            className={`p-2 rounded-md ${soundOn ? "text-blue-600 hover:bg-blue-50" : "text-slate-400 hover:bg-slate-100"}`}
            data-testid="sound-toggle"
          >
            {soundOn ? <SpeakerHigh className="w-5 h-5" /> : <SpeakerSlash className="w-5 h-5" />}
          </button>
        </div>

        <ChannelBar channels={channels} activeId={channelId} onSelect={selectChannel} isAdmin={isAdmin} onChanged={loadChannels} />

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" style={{ maxHeight: 500 }} data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-10">Brak wiadomości.</div>
          ) : messages.map((m) => {
            const mine = m.author_id === String(user?._id || user?.id);
            const myId = String(user?._id || user?.id);
            const reactions = m.reactions || {};
            const reactionKeys = Object.keys(reactions).filter((k) => (reactions[k] || []).length > 0);
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`${m.type === "poll" ? "max-w-[92%]" : "max-w-[75%]"} relative group`}>
                  <div className={`rounded-md px-3 py-2 ${
                    m.is_pilne ? "bg-red-100 border-2 border-red-400"
                               : (mine ? "bg-blue-600 text-white" : "bg-slate-100 text-foreground")
                  }`}>
                    <div className={`flex items-center gap-2 text-[10px] font-semibold mb-0.5 ${
                      m.is_pilne ? "text-red-700" : (mine ? "text-blue-100" : "text-slate-500")
                    }`}>
                      {m.is_pilne && <Warning className="w-3 h-3" weight="fill" />}
                      <span>{m.author_name}</span>
                      {m.author_role === "admin" && !mine && <span className="px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded text-[9px]">Admin</span>}
                      <span className="opacity-70">· {fmtTime(m.created_at)}</span>
                    </div>
                    {m.type === "poll" ? (
                      <PollCard message={m} isAdmin={isAdmin} onUpdated={(upd) => setMessages((prev) => prev.map((x) => (x.id === upd.id ? upd : x)))} />
                    ) : (
                      <div className={`text-sm whitespace-pre-wrap ${m.is_pilne ? "text-red-900 font-medium" : ""}`}>{m.content}</div>
                    )}
                    {m.attachment && <MessageAttachment att={m.attachment} mine={mine} onOpenImage={setLightbox} />}
                  </div>

                  {/* Reactions row */}
                  {reactionKeys.length > 0 && (
                    <div className={`flex flex-wrap gap-1 mt-1 ${mine ? "justify-end" : "justify-start"}`}>
                      {reactionKeys.map((emoji) => {
                        const ids = reactions[emoji] || [];
                        const iReacted = ids.includes(myId);
                        return (
                          <button
                            key={emoji}
                            onClick={() => react(m.id, emoji)}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
                              iReacted ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-border text-slate-600"
                            }`}
                            data-testid={`reaction-${m.id}-${emoji}`}
                          >
                            <span>{emoji}</span>
                            <span className="font-semibold">{ids.length}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Emoji picker */}
                  {pickerFor === m.id && (
                    <div className={`absolute z-20 -top-9 ${mine ? "right-0" : "left-0"} bg-white border border-border rounded-full shadow-lg px-2 py-1 flex gap-1`} data-testid={`picker-${m.id}`}>
                      {REACTION_EMOJIS.map((e) => (
                        <button key={e} onClick={() => react(m.id, e)}
                          className="hover:bg-slate-100 rounded-full w-7 h-7 text-lg leading-none" title={e}>
                          {e}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Hover actions */}
                  <div className={`absolute -top-2 ${mine ? "-left-8" : "-right-8"} opacity-0 group-hover:opacity-100 flex items-center gap-1`}>
                    <button
                      onClick={() => setPickerFor(pickerFor === m.id ? null : m.id)}
                      className="p-1 rounded-full bg-white border border-border shadow-sm hover:bg-slate-100"
                      title="Reaguj"
                      data-testid={`react-btn-${m.id}`}
                    >
                      <Smiley className="w-3.5 h-3.5 text-slate-600" />
                    </button>
                    {(isAdmin || mine) && (
                      <button onClick={() => delMsg(m.id)}
                        className="p-1 rounded-full bg-white border border-border shadow-sm hover:bg-red-50 text-red-600"
                        title="Usuń"
                      >
                        <Trash className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Composer */}
        <div className="border-t border-border p-3 space-y-2">
          {/* Typing indicator */}
          {typingUsers.length > 0 && (
            <div className="text-xs italic text-muted-foreground flex items-center gap-1" data-testid="typing-indicator">
              <span className="inline-flex gap-0.5">
                <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }}></span>
                <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }}></span>
                <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }}></span>
              </span>
              {typingUsers.length === 1
                ? `${typingUsers[0].name} pisze...`
                : `${typingUsers.map((u) => u.name).join(", ")} — piszą...`}
            </div>
          )}
          {isAdmin && (
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={isPilne} onChange={(e) => setIsPilne(e.target.checked)}
                className="w-4 h-4 accent-red-600" data-testid="chat-pilne" />
              <span className={`font-semibold ${isPilne ? "text-red-600" : "text-muted-foreground"}`}>
                <Warning className="w-3 h-3 inline mr-1" weight="bold" /> PILNE — wyświetl jako overlay + email
              </span>
            </label>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              onChange={onFileChange}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.xlsx,.xls,.txt,.csv"
              data-testid="chat-file-input"
            />
            <button
              onClick={onPickFile}
              disabled={uploading || !!attachment}
              className="w-10 h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md shrink-0 flex items-center justify-center disabled:opacity-50"
              title="Załącz plik"
              data-testid="chat-attach-btn"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowPoll(true)}
                className="w-10 h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md shrink-0 flex items-center justify-center"
                title="Utwórz ankietę"
                data-testid="chat-poll-btn"
              >
                <ChartBar className="w-5 h-5" />
              </button>
            )}
            <textarea
              value={text}
              onChange={handleInputChange}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={attachment ? "Dodaj komentarz do pliku... (opcjonalny)" : "Wpisz wiadomość... (Enter = wyślij, Shift+Enter = nowa linia)"}
              rows={2}
              className="flex-1 px-3 py-2 border border-border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
              data-testid="chat-input"
            />
            <button onClick={send} disabled={sending || uploading || (!text.trim() && !attachment)}
              className="w-10 h-10 bg-[#2563EB] hover:bg-blue-700 text-white rounded-md disabled:opacity-50 flex items-center justify-center shrink-0"
              data-testid="chat-send">
              <PaperPlaneRight className="w-5 h-5" weight="fill" />
            </button>
          </div>

          {/* Attachment preview above composer */}
          {(attachment || uploading) && (
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-border rounded-md text-xs" data-testid="attachment-preview">
              {uploading ? (
                <span className="text-muted-foreground">Wgrywanie pliku...</span>
              ) : (
                <>
                  {isImg(attachment.content_type) ? (
                    <AuthImage
                      url={`${window.location.origin}${attachment.url}`}
                      alt=""
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    <FileIcon className="w-5 h-5 text-slate-500" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{attachment.name}</div>
                    <div className="text-muted-foreground">{fmtBytes(attachment.size)}</div>
                  </div>
                  <button onClick={() => setAttachment(null)} className="p-1 hover:bg-slate-200 rounded" title="Usuń">
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Online sidebar */}
      <div className="bg-white border border-border rounded-md shadow-sm p-3" data-testid="online-list">
        <h3 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-3">
          Online · {online.length}
        </h3>
        {online.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nikt nie jest w tej chwili online.</p>
        ) : (
          <div className="space-y-2">
            {online.map((o) => (
              <div key={o.user_id} className="flex items-center gap-2 text-sm">
                <Circle className="w-2.5 h-2.5 text-emerald-500" weight="fill" />
                <span className="font-medium truncate">{o.name}</span>
                {o.role === "admin" && <span className="text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">A</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {lightbox && <ImageLightbox item={lightbox} onClose={() => setLightbox(null)} />}
      {showPoll && (
        <CreatePollModal
          channelId={channelId}
          channelName={channels.find((c) => c.id === channelId)?.name || "Ogólny"}
          onClose={() => setShowPoll(false)}
          onCreated={async () => { setShowPoll(false); await loadAll(); }}
        />
      )}
    </div>
  );
}

/** Fullscreen image preview with backdrop, close on Esc / backdrop click, download button. */
function ImageLightbox({ item, onClose }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchBlobUrl(item.url).then((u) => { if (!cancelled) setSrc(u); }).catch(() => {});
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { cancelled = true; window.removeEventListener("keydown", onKey); };
  }, [item.url, onClose]);
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 flex flex-col items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="chat-lightbox"
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/70 to-transparent"
        onClick={(e) => e.stopPropagation()}>
        <div className="text-white text-sm font-medium truncate max-w-[60vw]">
          {item.name} <span className="text-white/60 text-xs">· {fmtBytes(item.size)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadAuth(item.url, item.name)}
            className="inline-flex items-center gap-1.5 text-white text-sm px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 border border-white/20"
            data-testid="lightbox-download"
          >
            <Download className="w-4 h-4" /> Pobierz
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white"
            aria-label="Zamknij"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      {/* Image */}
      {src ? (
        <img
          src={src}
          alt={item.name}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full object-contain shadow-2xl cursor-default"
        />
      ) : (
        <div className="text-white/70 text-sm animate-pulse">Ładowanie obrazu...</div>
      )}
    </div>
  );
}
